import { clearAuthSession, getAccessToken, getAuthSession, updateAuthSession } from './auth';

let refreshPromise = null;
let hasWarnedMissingApiBaseUrl = false;

export function getApiBaseUrl() {
  const configuredBaseUrl = import.meta.env.VITE_API_BASE_URL;

  if (configuredBaseUrl) {
    return configuredBaseUrl;
  }

  if (import.meta.env.DEV) {
    return 'http://localhost:5201';
  }

  if (!hasWarnedMissingApiBaseUrl && typeof console !== 'undefined') {
    console.warn('[admin-web] VITE_API_BASE_URL is missing in production. Falling back to https://eventlistapi.2pix.gr.');
    hasWarnedMissingApiBaseUrl = true;
  }

  return 'https://eventlistapi.2pix.gr';
}

function buildHeaders(headers = {}, body) {
  const finalHeaders = { ...headers };
  const isMultipartBody = typeof FormData !== 'undefined' && body instanceof FormData;
  const isBinaryBody =
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    (typeof File !== 'undefined' && body instanceof File) ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams);

  if (!isMultipartBody && !isBinaryBody && finalHeaders['Content-Type'] == null && body != null) {
    finalHeaders['Content-Type'] = 'application/json';
  }

  return finalHeaders;
}

function redirectToLogin() {
  if (typeof window === 'undefined') {
    return;
  }

  const baseName = import.meta.env.VITE_APP_BASE_NAME || '';
  const normalizedBaseName = baseName.endsWith('/') ? baseName.slice(0, -1) : baseName;
  const loginPath = `${normalizedBaseName}/login`;
  const loginUrl = `${window.location.origin}${loginPath}`;

  if (window.location.pathname !== loginPath && !window.location.pathname.endsWith('/login')) {
    window.location.assign(loginUrl);
  }
}

async function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const session = getAuthSession();
    const refreshToken = session?.refreshToken;

    if (!refreshToken) {
      clearAuthSession();
      redirectToLogin();
      return false;
    }

    const response = await fetch(`${getApiBaseUrl()}/api/auth/refresh`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) {
      clearAuthSession();
      redirectToLogin();
      return false;
    }

    const data = await response.json();

    updateAuthSession({
      accessToken: data?.accessToken,
      refreshToken: data?.refreshToken,
      expiresAtUtc: data?.expiresAtUtc,
      refreshTokenExpiresAtUtc: data?.refreshTokenExpiresAtUtc,
      sessionId: data?.sessionId,
      user: data?.user
    });

    return true;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function performRequest(path, options, auth) {
  const finalHeaders = buildHeaders(options.headers, options.body);

  if (auth) {
    const token = getAccessToken();
    if (token) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
  }

  return fetch(`${getApiBaseUrl()}${path}`, {
    ...options,
    headers: finalHeaders
  });
}

export async function apiRequest(path, options = {}) {
  const { auth = true, retryOnAuthFailure = true, ...rest } = options;

  let response = await performRequest(path, rest, auth);

  if (auth && retryOnAuthFailure && response.status === 401) {
    const refreshed = await refreshAccessToken();

    if (refreshed) {
      response = await performRequest(path, rest, auth);
    }
  }

  return response;
}
